import { totalModelTokens } from './tokenMetrics';

const toNumber = (value) => Number(value || 0);

const makeShare = (cost, totalCost) => (
  totalCost > 0 ? Number(((cost / totalCost) * 100).toFixed(2)) : 0
);

const finalizeRows = (rows) => {
  const totalCost = rows.reduce((sum, row) => sum + toNumber(row.total_cost_usd), 0);
  return rows
    .map(row => ({
      ...row,
      total_tokens: totalModelTokens(row.prompt_tokens, row.completion_tokens),
      share_of_spend_pct: makeShare(toNumber(row.total_cost_usd), totalCost),
    }))
    .sort((left, right) => right.total_cost_usd - left.total_cost_usd || left.label.localeCompare(right.label));
};

export function aggregateAgentUsageRows(rows) {
  if (!Array.isArray(rows)) throw new Error('aggregateAgentUsageRows requires an array');

  const byAgent = new Map();
  rows.forEach((row) => {
    const rawAgentKey = row.agent_id == null ? row.agent_name : row.agent_id;
    const key = `agent:${rawAgentKey || 'unknown'}`;
    const existing = byAgent.get(key) || {
      key,
      label: row.agent_name || String(rawAgentKey || 'Unknown agent'),
      filterValue: row.agent_name || String(rawAgentKey || 'Unknown agent'),
      total_cost_usd: 0,
      trace_count: null,
      avg_cost_per_trace_usd: null,
      previous_total_cost_usd: null,
      delta_cost_usd: null,
      delta_cost_pct: null,
      prompt_tokens: 0,
      completion_tokens: 0,
      cached_tokens: 0,
    };

    existing.total_cost_usd += toNumber(row.cost_usd);
    existing.prompt_tokens += toNumber(row.input_tokens);
    existing.completion_tokens += toNumber(row.output_tokens);
    existing.cached_tokens += toNumber(row.cached_tokens);
    byAgent.set(key, existing);
  });

  return finalizeRows([...byAgent.values()]);
}

export function aggregateModelUsageRows(rows) {
  if (!Array.isArray(rows)) throw new Error('aggregateModelUsageRows requires an array');

  const byModel = new Map();
  rows.forEach((row) => {
    const provider = row.llm_provider || row.provider || '';
    const modelName = row.model_name || 'Unknown model';
    const key = `model:${provider || 'unknown'}:${modelName}`;
    const existing = byModel.get(key) || {
      key,
      label: provider && provider !== 'Unknown' ? `${provider} / ${modelName}` : modelName,
      filterValue: modelName,
      total_cost_usd: 0,
      trace_count: null,
      avg_cost_per_trace_usd: null,
      previous_total_cost_usd: null,
      delta_cost_usd: null,
      delta_cost_pct: null,
      prompt_tokens: 0,
      completion_tokens: 0,
      cached_tokens: 0,
    };

    existing.total_cost_usd += toNumber(row.cost_usd);
    existing.prompt_tokens += toNumber(row.input_tokens);
    existing.completion_tokens += toNumber(row.output_tokens);
    existing.cached_tokens += toNumber(row.cached_tokens);
    byModel.set(key, existing);
  });

  return finalizeRows([...byModel.values()]);
}

export function normalizeWorkflowUsageRows(rows) {
  if (!Array.isArray(rows)) throw new Error('normalizeWorkflowUsageRows requires an array');

  return rows.map(row => ({
    key: row.workflow_id,
    label: row.canonical_name || row.workflow_id,
    filterValue: row.canonical_name || row.workflow_id,
    total_cost_usd: row.cost_usd,
    trace_count: row.trace_count,
    avg_cost_per_trace_usd: row.avg_cost_per_trace,
    previous_total_cost_usd: null,
    delta_cost_usd: null,
    delta_cost_pct: null,
    prompt_tokens: row.input_tokens,
    completion_tokens: row.output_tokens,
    cached_tokens: row.cached_tokens,
    total_tokens: totalModelTokens(row.input_tokens, row.output_tokens),
    share_of_spend_pct: row.share_of_spend_pct,
  }));
}
